import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../hooks/useAuthStore';
import {
  Users, Plus, Edit2, X, ToggleLeft, ToggleRight, Search, AlertTriangle, Calendar, Video, MapPin,
} from 'lucide-react';
import clsx from 'clsx';

interface Minister {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  spiritual_gifts: string | null;
  session_types_offered: string | null;
  availability_status: string;
  status: string;
  active_candidates: number;
  completed_candidates: number;
  max_candidates: number;
}

interface ScheduleDay {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DEFAULT_SCHEDULE: ScheduleDay[] = Array.from({ length: 7 }, (_, i) => ({
  dayOfWeek: i,
  startTime: '09:00',
  endTime: '17:00',
  isAvailable: i >= 1 && i <= 5,
}));

interface MinistersResponse {
  ministers: Minister[];
  total: number;
}

interface MinisterForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  spiritualGifts: string;
  maxCandidates: number;
}

const emptyForm: MinisterForm = {
  firstName: '', lastName: '', email: '', phone: '', password: '', spiritualGifts: '', maxCandidates: 5,
};

export function MinistersPage() {
  const [ministers, setMinisters] = useState<Minister[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MinisterForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Schedule & session types state
  const [schedule, setSchedule] = useState<ScheduleDay[]>(DEFAULT_SCHEDULE);
  const [sessionTypes, setSessionTypes] = useState<string[]>([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  const fetchMinisters = useCallback(async () => {
    setIsLoading(true);
    const result = await apiRequest<MinistersResponse>('/ministers');
    if (result.success && result.data) {
      setMinisters(result.data.ministers);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchMinisters();
  }, [fetchMinisters]);

  const handleCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const fetchMinisterConfig = async (ministerId: string, ministerData: Minister) => {
    setLoadingSchedule(true);
    // Fetch schedule
    const schedResult = await apiRequest<{ schedules: Array<{ day_of_week: number; start_time: string; end_time: string; is_available: number }> }>(`/ministers/${ministerId}/schedule`);
    if (schedResult.success && schedResult.data && schedResult.data.schedules.length > 0) {
      const merged = DEFAULT_SCHEDULE.map(d => {
        const existing = schedResult.data!.schedules.find(s => s.day_of_week === d.dayOfWeek);
        return existing ? { dayOfWeek: existing.day_of_week, startTime: existing.start_time, endTime: existing.end_time, isAvailable: !!existing.is_available } : d;
      });
      setSchedule(merged);
    } else {
      setSchedule(DEFAULT_SCHEDULE);
    }
    // Parse session types
    try {
      const types = ministerData.session_types_offered ? JSON.parse(ministerData.session_types_offered) : [];
      setSessionTypes(Array.isArray(types) ? types : []);
    } catch {
      setSessionTypes([]);
    }
    setLoadingSchedule(false);
  };

  const handleEdit = (m: Minister) => {
    setEditingId(m.id);
    setForm({
      firstName: m.first_name,
      lastName: m.last_name,
      email: m.email,
      phone: m.phone || '',
      password: '',
      spiritualGifts: m.spiritual_gifts || '',
      maxCandidates: m.max_candidates,
    });
    setError('');
    setShowModal(true);
    fetchMinisterConfig(m.id, m);
  };

  const handleSave = async () => {
    if (!form.firstName || !form.lastName || !form.email) {
      setError('Nombre, apellido y email son obligatorios');
      return;
    }
    if (!editingId && !form.password) {
      setError('La contraseña es obligatoria para nuevos ministros');
      return;
    }

    setSaving(true);
    setError('');

    let createdId: string | null = null;
    if (editingId) {
      const body: Record<string, unknown> = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone || null,
        spiritualGifts: form.spiritualGifts || null,
        maxCandidates: form.maxCandidates,
      };
      if (form.password) body.password = form.password;

      const result = await apiRequest(`/ministers/${editingId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (!result.success) {
        setError(result.error || 'Error al actualizar');
        setSaving(false);
        return;
      }
    } else {
      const result = await apiRequest<{ id: string }>('/ministers', {
        method: 'POST',
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone || null,
          password: form.password,
          spiritualGifts: form.spiritualGifts || null,
          maxCandidates: form.maxCandidates,
        }),
      });
      if (!result.success) {
        setError(result.error || 'Error al crear');
        setSaving(false);
        return;
      }
      createdId = result.data?.id || null;
    }

    // Save schedule and session types
    const targetId = editingId || createdId;
    if (targetId) {
      await apiRequest(`/ministers/${targetId}/schedule`, {
        method: 'PUT',
        body: JSON.stringify({ schedules: schedule }),
      });
      await apiRequest(`/ministers/${targetId}/session-types`, {
        method: 'PUT',
        body: JSON.stringify({ sessionTypes }),
      });
    }

    setSaving(false);
    setShowModal(false);
    await fetchMinisters();
  };

  const handleToggleAvailability = async (m: Minister) => {
    const newStatus = m.availability_status === 'available' ? 'unavailable' : 'available';
    await apiRequest(`/ministers/${m.id}/availability`, {
      method: 'PATCH',
      body: JSON.stringify({ availability: newStatus }),
    });
    await fetchMinisters();
  };

  const filtered = ministers.filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      m.first_name.toLowerCase().includes(s) ||
      m.last_name.toLowerCase().includes(s) ||
      m.email.toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ministros</h1>
        <button onClick={handleCreate} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nuevo Ministro
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o email..."
          className="input pl-10 w-full"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card p-12">
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <h3 className="empty-state-title">
              {ministers.length === 0 ? 'No hay ministros registrados' : 'Sin resultados'}
            </h3>
            <p className="empty-state-description">
              {ministers.length === 0 ? 'Crea el primer ministro para comenzar' : 'Intenta con otro término de búsqueda'}
            </p>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-dark-700/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nombre</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dones</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Candidatos</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Config</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Disponibilidad</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                {filtered.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-dark-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{m.first_name} {m.last_name}</p>
                        {m.phone && <p className="text-xs text-gray-500">{m.phone}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{m.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{m.spiritual_gifts || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={clsx(
                        'text-sm font-medium',
                        m.active_candidates >= m.max_candidates ? 'text-danger-600' : 'text-gray-900 dark:text-white'
                      )}>
                        {m.active_candidates}/{m.max_candidates}
                      </span>
                      {m.completed_candidates > 0 && (
                        <span className="text-xs text-gray-400 ml-1">({m.completed_candidates} completados)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const types = (() => { try { return JSON.parse(m.session_types_offered || '[]'); } catch { return []; } })();
                        const hasSessionTypes = Array.isArray(types) && types.length > 0;
                        return hasSessionTypes ? (
                          <span className="text-xs text-success-600 dark:text-success-400" title={types.join(', ')}>
                            {types.includes('in_person') && <MapPin className="w-3.5 h-3.5 inline mr-0.5" />}
                            {types.includes('virtual') && <Video className="w-3.5 h-3.5 inline mr-0.5" />}
                          </span>
                        ) : (
                          <span className="text-xs text-warning-600 dark:text-warning-400 flex items-center justify-center gap-0.5" title="Sin configurar">
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleAvailability(m)}
                        className="inline-flex items-center gap-1 text-sm"
                        title={m.availability_status === 'available' ? 'Disponible' : 'No disponible'}
                      >
                        {m.availability_status === 'available' ? (
                          <><ToggleRight className="w-6 h-6 text-success-500" /><span className="text-success-600 text-xs">Disponible</span></>
                        ) : (
                          <><ToggleLeft className="w-6 h-6 text-gray-400" /><span className="text-gray-500 text-xs">No disponible</span></>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleEdit(m)} className="btn-ghost p-1.5" title="Editar">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {editingId ? 'Editar Ministro' : 'Nuevo Ministro'}
                </h3>
                <button onClick={() => setShowModal(false)} className="btn-ghost p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                {error && <div className="text-sm text-danger-600 bg-danger-50 dark:bg-danger-500/10 rounded-lg p-3">{error}</div>}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
                    <input type="text" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="input w-full" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido *</label>
                    <input type="text" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="input w-full" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input w-full" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input w-full" placeholder="+57..." />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contraseña {editingId ? '(dejar vacío para no cambiar)' : '*'}
                  </label>
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input w-full" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dones Espirituales</label>
                  <input type="text" value={form.spiritualGifts} onChange={(e) => setForm({ ...form, spiritualGifts: e.target.value })} className="input w-full" placeholder="Ej: Discernimiento, Intercesión" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Máx. Candidatos</label>
                  <input type="number" value={form.maxCandidates} onChange={(e) => setForm({ ...form, maxCandidates: parseInt(e.target.value) || 5 })} className="input w-full" min={1} max={50} />
                </div>

                {/* Session Types */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                    <Video className="w-4 h-4" /> Tipos de Sesión
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={sessionTypes.includes('in_person')}
                        onChange={(e) => {
                          setSessionTypes(prev => e.target.checked ? [...prev, 'in_person'] : prev.filter(t => t !== 'in_person'));
                        }}
                        className="rounded border-gray-300"
                      />
                      <MapPin className="w-4 h-4" /> Presencial
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={sessionTypes.includes('virtual')}
                        onChange={(e) => {
                          setSessionTypes(prev => e.target.checked ? [...prev, 'virtual'] : prev.filter(t => t !== 'virtual'));
                        }}
                        className="rounded border-gray-300"
                      />
                      <Video className="w-4 h-4" /> Virtual
                    </label>
                  </div>
                </div>

                {/* Schedule Grid */}
                {editingId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" /> Horario Disponible
                    </label>
                    {loadingSchedule ? (
                      <div className="flex justify-center py-4"><div className="spinner" /></div>
                    ) : (
                      <div className="space-y-2">
                        {schedule.map((day, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <label className="flex items-center gap-1.5 w-24">
                              <input
                                type="checkbox"
                                checked={day.isAvailable}
                                onChange={(e) => {
                                  const updated = [...schedule];
                                  updated[i] = { ...updated[i], isAvailable: e.target.checked };
                                  setSchedule(updated);
                                }}
                                className="rounded border-gray-300"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">{DAY_NAMES[day.dayOfWeek].slice(0, 3)}</span>
                            </label>
                            <input
                              type="time"
                              value={day.startTime}
                              onChange={(e) => {
                                const updated = [...schedule];
                                updated[i] = { ...updated[i], startTime: e.target.value };
                                setSchedule(updated);
                              }}
                              className="input text-xs py-1 px-2 w-24"
                              disabled={!day.isAvailable}
                            />
                            <span className="text-xs text-gray-400">a</span>
                            <input
                              type="time"
                              value={day.endTime}
                              onChange={(e) => {
                                const updated = [...schedule];
                                updated[i] = { ...updated[i], endTime: e.target.value };
                                setSchedule(updated);
                              }}
                              className="input text-xs py-1 px-2 w-24"
                              disabled={!day.isAvailable}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-dark-700">
                <button onClick={() => setShowModal(false)} className="btn-ghost">Cancelar</button>
                <button onClick={handleSave} disabled={saving} className="btn-primary">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
